import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  PlusIcon,
  CaretUpIcon,
  CaretDownIcon,
  TrashIcon,
  QuestionIcon,
  LinkIcon,
  LinkBreakIcon,
  CopyIcon,
  SparkleIcon,
  CircleNotchIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MultiSelect } from '@/components/common/MultiSelect';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { ProductGroup } from '@/api/rebates/useProductGroups';
import { formatThresholdRange } from '@/utils/rebateUtils';
import { useSuggestGrowthBaseline } from '@/api/rpc/hooks/rebates/useGrowthBaselineSuggestion';
import { formatCurrency } from '@erp/shared-utils';

import PrimaryButton from '@/components/common/PrimaryButton';
import {
  RebateRule,
  ProductInclusionType,
  ThresholdType,
  ThresholdTypeDescriptions,
  DivisionSimpleDTO,
} from '@erp/shared-types';
import { cn } from '@/lib/utils';

/**
 * Format a Date to YYYY-MM-DD string using local time (avoids timezone shifts)
 */
function formatDateToLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

interface SimpleProductGroup {
  id: string;
  name: string;
}

interface RebateRuleInlineFormProps {
  rule: RebateRule;
  divisions: DivisionSimpleDTO[];
  productGroups: ProductGroup[];
  onUpdate: (updatedRule: RebateRule) => void;
  onValidationChange?: (isValid: boolean) => void;
  tierStatus?: { estimates?: number; predictions?: number; reported?: number };
  // Agreement dates for rule duration bounds
  agreementStartDate?: string;
  agreementEndDate?: string;
}

// Product Group Selector Component
const ProductGroupSelector: React.FC<{
  label: string;
  placeholder: string;
  inclusionType: ProductInclusionType;
  onInclusionTypeChange: (type: ProductInclusionType) => void;
  selectedGroups: SimpleProductGroup[];
  onGroupsChange: (groups: SimpleProductGroup[]) => void;
  inclusionTypeName: string;
  required?: boolean;
  productGroups: ProductGroup[];
}> = ({
  label,
  placeholder,
  inclusionType,
  onInclusionTypeChange,
  selectedGroups,
  onGroupsChange,
  inclusionTypeName,
  required = false,
  productGroups,
}) => {
  const isQualifying = label.includes('Qualifying');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Label className="text-sm font-semibold">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      </div>
      <div className="flex gap-4">
        <MultiSelect
          placeholder={placeholder}
          options={productGroups.map((pg) => ({
            id: pg.id,
            label: pg.name,
            value: pg,
          }))}
          value={selectedGroups.map((pg) => ({
            id: pg.id,
            label: pg.name,
            value: pg,
          }))}
          onChange={(selected) => {
            const selectedProductGroups = selected.map(
              (option) => option.value as SimpleProductGroup,
            );
            onGroupsChange(selectedProductGroups);
          }}
          searchPlaceholder="Search product groups"
          renderOptionLabel={(option) => {
            const productGroup = option.value as ProductGroup;
            return (
              <div className="flex items-center gap-2">
                <span>{option.label}</span>
                {productGroup?.isCatchAll && (
                  <Badge variant="secondary" className="text-xs shrink-0">
                    Default
                  </Badge>
                )}
              </div>
            );
          }}
          renderSelectedDisplay={(selectedItems, totalCount) => {
            const isAllProducts =
              (inclusionType === ProductInclusionType.INCLUSIVE &&
                selectedItems.length === totalCount &&
                totalCount > 0) ||
              (inclusionType === ProductInclusionType.EXCLUSIVE &&
                selectedItems.length === 0);

            if (isAllProducts) {
              return (
                <div className="flex gap-1 mb-2">
                  <Badge variant="outline">All Products</Badge>
                </div>
              );
            }

            const visibleChips = selectedItems.slice(0, 5);
            const remainingCount = selectedItems.length - visibleChips.length;

            return (
              <div
                className={cn(
                  'flex flex-wrap gap-1',
                  selectedItems.length > 0 ? 'mb-2' : '',
                )}
              >
                {visibleChips.map((option) => {
                  const productGroup = option.value as ProductGroup;
                  return (
                    <div key={option.id} className="flex items-center gap-1">
                      <Badge
                        variant="outline"
                        className="text-xs px-2 py-1 bg-transparent border-border text-foreground"
                      >
                        {option.label}
                      </Badge>
                      {productGroup?.isCatchAll && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          Default
                        </Badge>
                      )}
                    </div>
                  );
                })}
                {remainingCount > 0 && (
                  <Badge
                    variant="outline"
                    className="text-xs px-2 py-1 bg-muted text-muted-foreground border-border"
                  >
                    +{remainingCount} more
                  </Badge>
                )}
              </div>
            );
          }}
        />
        <div className="flex items-center gap-4 self-start">
          <div>
            <Select
              name={inclusionTypeName}
              value={inclusionType}
              onValueChange={(value) =>
                onInclusionTypeChange(value as ProductInclusionType)
              }
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value={ProductInclusionType.INCLUSIVE}>
                  Include
                </SelectItem>
                <SelectItem value={ProductInclusionType.EXCLUSIVE}>
                  Exclude
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <QuestionIcon className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <div className="space-y-2">
                  <p className="font-semibold">Include vs Exclude:</p>
                  <p>
                    <strong>Include:</strong> Only the selected product groups
                    will be{' '}
                    {isQualifying
                      ? 'used for threshold calculations'
                      : 'eligible for rebates'}
                  </p>
                  <p>
                    <strong>Exclude:</strong> All product groups except the
                    selected ones will be{' '}
                    {isQualifying
                      ? 'used for threshold calculations'
                      : 'eligible for rebates'}
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
};

export const RebateRuleInlineForm: React.FC<RebateRuleInlineFormProps> = ({
  rule,
  divisions,
  productGroups,
  onUpdate,
  onValidationChange,
  tierStatus,
  agreementStartDate,
  agreementEndDate,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    name: rule.name,
    isManual: rule.isManual,
    thresholdType: rule.thresholdType,
    growthBaseline: rule.growthBaseline || '',
    discountPercentage: rule.discountPercentage?.toString() || '',
    isRetroactiveToDollarOne: rule.isRetroactiveToDollarOne || false,
    notes: rule.notes || '',
    qualifyingInclusionType:
      rule.qualifyingInclusionType || ProductInclusionType.INCLUSIVE,
    affectedInclusionType:
      rule.affectedInclusionType || ProductInclusionType.INCLUSIVE,
    qualifyingProductGroups: rule.qualifyingProductGroups || [],
    affectedProductGroups: rule.affectedProductGroups || [],
    selectedDivisions: rule.divisions || [],
    // Rule-specific date range (null means use agreement dates)
    startDate: rule.startDate || null,
    endDate: rule.endDate || null,
  });

  // Track if custom duration is enabled
  const [hasCustomDuration, setHasCustomDuration] = useState(
    Boolean(rule.startDate || rule.endDate),
  );

  const [tiers, setTiers] = useState(
    rule.tiers?.map((t) => ({
      id: t.id,
      tierOrder: t.tierOrder,
      thresholdValue: t.thresholdValue,
      rebateValue: t.rebateValue,
    })) || [{ id: '', tierOrder: 1, thresholdValue: '', rebateValue: '' }],
  );

  const [expandedTiers, setExpandedTiers] = useState<Set<number>>(new Set([0]));

  // Growth baseline suggestion
  const [suggestionPopoverOpen, setSuggestionPopoverOpen] = useState(false);
  const suggestBaselineMutation = useSuggestGrowthBaseline();

  // Helper to check if qualifying and affected groups are the same
  const areProductGroupsSame = (
    qualifying: SimpleProductGroup[],
    affected: SimpleProductGroup[],
    qualifyingType: ProductInclusionType,
    affectedType: ProductInclusionType,
  ): boolean => {
    if (qualifyingType !== affectedType) return false;
    if (qualifying.length !== affected.length) return false;
    const qualifyingIds = new Set(qualifying.map((g) => g.id));
    return affected.every((g) => qualifyingIds.has(g.id));
  };

  // State to track if product groups should be combined
  const [combineProductGroups, setCombineProductGroups] = useState(() =>
    areProductGroupsSame(
      rule.qualifyingProductGroups || [],
      rule.affectedProductGroups || [],
      (rule.qualifyingInclusionType as ProductInclusionType) ||
        ProductInclusionType.INCLUSIVE,
      (rule.affectedInclusionType as ProductInclusionType) ||
        ProductInclusionType.INCLUSIVE,
    ),
  );

  // Memoized tier validation (same as modal)
  const tierValidation = useMemo(() => {
    const errors: {
      [tierIndex: number]: { thresholdValue?: string; rebateValue?: string };
    } = {};
    const thresholdValues: number[] = [];

    tiers.forEach((tier, index) => {
      const thresholdNum = parseFloat(tier.thresholdValue);
      const rebateNum = parseFloat(tier.rebateValue);

      // Validate threshold value
      if (tier.thresholdValue && (isNaN(thresholdNum) || thresholdNum < 0)) {
        errors[index] = {
          ...errors[index],
          thresholdValue: 'Must be a positive number',
        };
      } else if (tier.thresholdValue && thresholdNum >= 0) {
        if (formData.thresholdType === ThresholdType.GROWTH) {
          // Growth thresholds represent absolute percentage of baseline
          // e.g., 105 = 105% of baseline, 90 = 90% of baseline
          if (thresholdNum > 500) {
            errors[index] = {
              ...errors[index],
              thresholdValue:
                'Percentage of baseline seems too high (max 500%)',
            };
          }
        }
        thresholdValues.push(thresholdNum);
      }

      // Validate rebate value
      if (
        tier.rebateValue &&
        (isNaN(rebateNum) || rebateNum < 0 || rebateNum > 100)
      ) {
        errors[index] = {
          ...errors[index],
          rebateValue: 'Must be between 0 and 100',
        };
      }
    });

    // Check for duplicate threshold values
    const duplicateThresholds = thresholdValues.filter(
      (value, index, arr) => arr.indexOf(value) !== index,
    );

    if (duplicateThresholds.length > 0) {
      tiers.forEach((tier, index) => {
        const thresholdNum = parseFloat(tier.thresholdValue);
        if (duplicateThresholds.includes(thresholdNum)) {
          errors[index] = {
            ...errors[index],
            thresholdValue: 'Duplicate threshold value',
          };
        }
      });
    }

    // Check for logical threshold progression
    const sortedTiers = [...tiers]
      .map((tier, index) => ({ ...tier, originalIndex: index }))
      .filter(
        (tier) =>
          tier.thresholdValue && !isNaN(parseFloat(tier.thresholdValue)),
      )
      .sort((a, b) => a.tierOrder - b.tierOrder);

    for (let i = 1; i < sortedTiers.length; i++) {
      const prevThreshold = parseFloat(sortedTiers[i - 1].thresholdValue);
      const currentThreshold = parseFloat(sortedTiers[i].thresholdValue);

      if (currentThreshold <= prevThreshold) {
        const errorMessage =
          formData.thresholdType === ThresholdType.GROWTH
            ? 'Percentage of baseline must be higher than previous tier'
            : 'Must be higher than previous tier';

        errors[sortedTiers[i].originalIndex] = {
          ...errors[sortedTiers[i].originalIndex],
          thresholdValue: errorMessage,
        };
      }
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0,
    };
  }, [formData.thresholdType, tiers]);

  // Memoized product group validation
  const productGroupValidation = useMemo(() => {
    const errors: {
      qualifyingProductGroups?: string;
      affectedProductGroups?: string;
    } = {};

    // Validate qualifying product groups
    if (formData.qualifyingInclusionType === ProductInclusionType.INCLUSIVE) {
      if (formData.qualifyingProductGroups.length === 0) {
        errors.qualifyingProductGroups = combineProductGroups
          ? 'Must select at least one product group when using inclusive mode'
          : 'Must select at least one product group when using inclusive mode';
      }
    } else if (
      formData.qualifyingInclusionType === ProductInclusionType.EXCLUSIVE
    ) {
      if (
        formData.qualifyingProductGroups.length >= productGroups.length &&
        productGroups.length > 0
      ) {
        errors.qualifyingProductGroups = combineProductGroups
          ? 'Cannot exclude all product groups - at least one must remain'
          : 'Cannot exclude all product groups - at least one must remain for threshold calculations';
      }
    }

    // Only validate affected product groups separately when not combined
    if (!combineProductGroups) {
      if (formData.affectedInclusionType === ProductInclusionType.INCLUSIVE) {
        if (formData.affectedProductGroups.length === 0) {
          errors.affectedProductGroups =
            'Must select at least one product group when using inclusive mode';
        }
      } else if (
        formData.affectedInclusionType === ProductInclusionType.EXCLUSIVE
      ) {
        if (
          formData.affectedProductGroups.length >= productGroups.length &&
          productGroups.length > 0
        ) {
          errors.affectedProductGroups =
            'Cannot exclude all product groups - at least one must remain for rebate application';
        }
      }
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0,
    };
  }, [
    formData.qualifyingProductGroups,
    formData.qualifyingInclusionType,
    formData.affectedProductGroups,
    formData.affectedInclusionType,
    productGroups,
    combineProductGroups,
  ]);

  // Comprehensive validation errors
  const validationErrors = useMemo(() => {
    const errors: {
      name?: string;
      growthBaseline?: string;
      divisions?: string;
      tiers?: {
        [tierIndex: number]: { thresholdValue?: string; rebateValue?: string };
      };
      productGroups?: {
        qualifyingProductGroups?: string;
        affectedProductGroups?: string;
      };
      discountPercentage?: string;
      dates?: string;
    } = {};

    // Validate name
    if (!formData.name.trim()) {
      errors.name = 'Rule name is required';
    }

    // Validate growth baseline (only required for growth threshold type and not manual)
    if (!formData.isManual && formData.thresholdType === ThresholdType.GROWTH) {
      if (!formData.growthBaseline.trim()) {
        errors.growthBaseline =
          'Growth baseline is required for growth-based rules';
      } else {
        const baselineNum = parseFloat(formData.growthBaseline);
        if (isNaN(baselineNum) || baselineNum < 0) {
          errors.growthBaseline = 'Growth baseline must be a positive number';
        }
      }
    }

    // Validate discount percentage (only for non-manual rules)
    const discountNum = parseFloat(formData.discountPercentage);
    if (
      formData.discountPercentage &&
      (isNaN(discountNum) || discountNum < 0 || discountNum > 100)
    ) {
      errors.discountPercentage =
        'Discount percentage must be between 0 and 100';
    }

    // Validate divisions
    if (formData.selectedDivisions.length === 0 && !formData.isManual) {
      errors.divisions = 'At least one division must be selected';
    }

    // Validate tiers (only if not manual)
    if (!formData.isManual) {
      const tierErrors: {
        [tierIndex: number]: { thresholdValue?: string; rebateValue?: string };
      } = {};
      let hasTierErrors = false;

      tiers.forEach((tier, index) => {
        if (!tier.thresholdValue) {
          tierErrors[index] = {
            ...tierErrors[index],
            thresholdValue: 'Threshold value is required',
          };
          hasTierErrors = true;
        }
        if (!tier.rebateValue) {
          tierErrors[index] = {
            ...tierErrors[index],
            rebateValue: 'Rebate value is required',
          };
          hasTierErrors = true;
        }
      });

      if (hasTierErrors) {
        errors.tiers = tierErrors;
      }

      // Add tier validation errors (numeric validation, duplicates, etc.)
      if (!tierValidation.isValid) {
        errors.tiers = { ...errors.tiers, ...tierValidation.errors };
      }
    }

    // Validate product groups (only if not manual)
    if (!formData.isManual) {
      const productGroupErrors: {
        qualifyingProductGroups?: string;
        affectedProductGroups?: string;
      } = {};
      if (productGroupValidation.errors.qualifyingProductGroups) {
        productGroupErrors.qualifyingProductGroups =
          productGroupValidation.errors.qualifyingProductGroups;
      }
      if (productGroupValidation.errors.affectedProductGroups) {
        productGroupErrors.affectedProductGroups =
          productGroupValidation.errors.affectedProductGroups;
      }

      if (Object.keys(productGroupErrors).length > 0) {
        errors.productGroups = productGroupErrors;
      }
    }

    // Validate custom duration dates - must have both or neither
    if (hasCustomDuration) {
      if (formData.startDate && !formData.endDate) {
        errors.dates = 'End date is required when start date is set';
      } else if (!formData.startDate && formData.endDate) {
        errors.dates = 'Start date is required when end date is set';
      }
    }

    return errors;
  }, [
    formData,
    tiers,
    tierValidation,
    productGroupValidation,
    hasCustomDuration,
  ]);

  const isFormValid = Object.keys(validationErrors).length === 0;

  // Notify parent when validation status changes
  useEffect(() => {
    onValidationChange?.(isFormValid);
  }, [isFormValid, onValidationChange]);

  // Helper function to create the update payload
  const createUpdatePayload = useCallback(
    (
      currentFormData: typeof formData,
      currentTiers: typeof tiers,
    ): RebateRule => {
      const tierData = currentFormData.isManual
        ? []
        : currentTiers.map((tier) => ({
            id: tier.id,
            tierOrder: tier.tierOrder,
            thresholdValue: tier.thresholdValue,
            rebateValue: tier.rebateValue,
            rebateRuleId: rule.id,
            createdAt: new Date(),
            updatedAt: new Date(),
          }));

      return {
        // Required fields from original rule
        id: rule.id,
        rebateAgreementId: rule.rebateAgreementId,
        sortOrder: rule.sortOrder,
        createdAt: rule.createdAt,
        updatedAt: new Date(), // Update timestamp
        // Editable fields
        name: currentFormData.name,
        isRetroactiveToDollarOne: currentFormData.isManual
          ? null
          : currentFormData.isRetroactiveToDollarOne,
        isManual: currentFormData.isManual,
        thresholdType: currentFormData.isManual
          ? null
          : (currentFormData.thresholdType as ThresholdType | null),
        growthBaseline: currentFormData.isManual
          ? null
          : currentFormData.growthBaseline || null,
        discountPercentage: currentFormData.discountPercentage as string,
        qualifyingInclusionType: currentFormData.isManual
          ? null
          : (currentFormData.qualifyingInclusionType as ProductInclusionType | null),
        affectedInclusionType: currentFormData.isManual
          ? null
          : (currentFormData.affectedInclusionType as ProductInclusionType | null),
        qualifyingProductGroups: currentFormData.isManual
          ? []
          : currentFormData.qualifyingProductGroups,
        affectedProductGroups: currentFormData.isManual
          ? []
          : currentFormData.affectedProductGroups,
        divisions: currentFormData.isManual
          ? []
          : currentFormData.selectedDivisions,
        tiers: tierData,
        notes: currentFormData.notes,
        // Rule-specific date range (null means use agreement dates)
        startDate: currentFormData.startDate,
        endDate: currentFormData.endDate,
      };
    },
    [rule],
  );

  const handleInputChange = (field: string, value: unknown) => {
    let newFormData = { ...formData, [field]: value };

    // When combined mode is on, sync qualifying and affected values
    if (combineProductGroups) {
      if (field === 'qualifyingProductGroups') {
        newFormData = {
          ...newFormData,
          affectedProductGroups: value as SimpleProductGroup[],
        };
      } else if (field === 'qualifyingInclusionType') {
        newFormData = {
          ...newFormData,
          affectedInclusionType: value as ProductInclusionType,
        };
      }
    }

    setFormData(newFormData);
    // Update parent immediately - no more re-render issues!
    onUpdate(createUpdatePayload(newFormData, tiers));
  };

  // Handle toggle of combine product groups
  const handleCombineToggle = (checked: boolean) => {
    setCombineProductGroups(checked);

    if (checked) {
      // When turning ON: sync affected to match qualifying
      const newFormData = {
        ...formData,
        affectedProductGroups: formData.qualifyingProductGroups,
        affectedInclusionType: formData.qualifyingInclusionType,
      };
      setFormData(newFormData);
      onUpdate(createUpdatePayload(newFormData, tiers));
    }
    // When turning OFF: keep current values (qualifying already populated both)
  };

  const handleTierChange = (
    index: number,
    field: 'thresholdValue' | 'rebateValue',
    value: string,
  ) => {
    const updated = [...tiers];
    updated[index] = { ...updated[index], [field]: value };
    setTiers(updated);
    // Update parent immediately - no more re-render issues!
    onUpdate(createUpdatePayload(formData, updated));
  };

  const addTier = () => {
    const updated = [
      ...tiers,
      {
        id: '',
        tierOrder: tiers.length + 1,
        thresholdValue: '',
        rebateValue: '',
      },
    ];
    setTiers(updated);
    setExpandedTiers((prev) => new Set(prev).add(tiers.length));
    // Update parent immediately - no more re-render issues!
    onUpdate(createUpdatePayload(formData, updated));
  };

  const removeTier = (index: number) => {
    if (tiers.length <= 1) return;
    const updated = tiers
      .filter((_, i) => i !== index)
      .map((tier, i) => ({ ...tier, tierOrder: i + 1 }));
    setTiers(updated);
    // Update parent immediately - no more re-render issues!
    onUpdate(createUpdatePayload(formData, updated));
  };

  const duplicateTier = (index: number) => {
    const tierToDuplicate = tiers[index];
    const newTier = {
      id: `temp-tier-${Date.now()}`,
      tierOrder: tiers.length + 1,
      thresholdValue: tierToDuplicate.thresholdValue,
      rebateValue: tierToDuplicate.rebateValue,
    };
    const updated = [...tiers, newTier];
    setTiers(updated);
    setExpandedTiers((prev) => new Set(prev).add(updated.length - 1));
    onUpdate(createUpdatePayload(formData, updated));
  };

  const toggleTierExpansion = (index: number) => {
    setExpandedTiers((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg border">
      {/* Basic Information */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Rule Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={validationErrors.name ? 'border-red-500' : ''}
              required
            />
            {validationErrors.name && (
              <p className="text-sm text-red-600">{validationErrors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ruleType">Rule Type *</Label>
            <Select
              value={
                formData.isManual
                  ? 'manual'
                  : (formData.thresholdType ?? undefined)
              }
              onValueChange={(value) => {
                if (value === 'manual') {
                  // Switching to manual: clear tiers since manual rules don't use them
                  setTiers([]);
                  const newFormData = {
                    ...formData,
                    isManual: true,
                    thresholdType: null,
                  };
                  setFormData(newFormData);
                  onUpdate(createUpdatePayload(newFormData, []));
                } else {
                  // Switching to volume/growth: add default tier if none exist
                  const newTiers =
                    tiers.length === 0
                      ? [
                          {
                            id: `temp-tier-${Date.now()}`,
                            tierOrder: 1,
                            thresholdValue: '0',
                            rebateValue: '5',
                          },
                        ]
                      : tiers;
                  if (tiers.length === 0) {
                    setTiers(newTiers);
                  }
                  const newFormData = {
                    ...formData,
                    isManual: false,
                    thresholdType: value as ThresholdType,
                  };
                  setFormData(newFormData);
                  onUpdate(createUpdatePayload(newFormData, newTiers));
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select rule type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ThresholdType.VOLUME}>
                  Volume - {ThresholdTypeDescriptions[ThresholdType.VOLUME]}
                </SelectItem>
                <SelectItem value={ThresholdType.GROWTH}>
                  Growth - {ThresholdTypeDescriptions[ThresholdType.GROWTH]}
                </SelectItem>
                <SelectItem value="manual">
                  Manual - No automatic tiers
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="discount">
              {formData.isManual ? 'Percentage (%)' : 'Discount (%)'}
            </Label>
            <Input
              id="discount"
              type="number"
              value={formData.discountPercentage}
              onChange={(e) =>
                handleInputChange('discountPercentage', e.target.value)
              }
              placeholder="e.g., 20.5"
              min="0"
              max="100"
              step="0.1"
            />
          </div>

          {!formData.isManual &&
            formData.thresholdType === ThresholdType.GROWTH && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="baseline">Growth Baseline ($) *</Label>
                  <Popover
                    open={suggestionPopoverOpen}
                    onOpenChange={setSuggestionPopoverOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          setSuggestionPopoverOpen(true);
                          suggestBaselineMutation.mutate({
                            agreementId: rule.rebateAgreementId,
                            divisionIds: formData.selectedDivisions.map(
                              (d) => d.id,
                            ),
                            qualifyingProductGroupIds:
                              formData.qualifyingProductGroups.map(
                                (pg) => pg.id,
                              ),
                            qualifyingInclusionType:
                              formData.qualifyingInclusionType as
                                | 'inclusive'
                                | 'exclusive',
                            affectedProductGroupIds:
                              formData.affectedProductGroups.map((pg) => pg.id),
                            affectedInclusionType:
                              formData.affectedInclusionType as
                                | 'inclusive'
                                | 'exclusive',
                          });
                        }}
                      >
                        <SparkleIcon className="h-3 w-3 mr-1" />
                        Suggest
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="start">
                      <div className="space-y-3">
                        <div className="font-medium text-sm">
                          Prior Year Baseline Suggestions
                        </div>
                        {suggestBaselineMutation.isPending && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                            <CircleNotchIcon className="h-4 w-4 animate-spin" />
                            Loading suggestions...
                          </div>
                        )}
                        {suggestBaselineMutation.isError && (
                          <div className="flex items-center gap-2 text-sm text-destructive">
                            <WarningCircleIcon className="h-4 w-4" />
                            Failed to load suggestions
                          </div>
                        )}
                        {suggestBaselineMutation.isSuccess &&
                          suggestBaselineMutation.data.status !== 'success' && (
                            <div className="text-sm text-muted-foreground py-2">
                              <WarningCircleIcon className="h-4 w-4 inline mr-2" />
                              {suggestBaselineMutation.data.message}
                            </div>
                          )}
                        {suggestBaselineMutation.isSuccess &&
                          suggestBaselineMutation.data.status === 'success' &&
                          suggestBaselineMutation.data.suggestions && (
                            <div className="space-y-2">
                              {suggestBaselineMutation.data
                                .priorYearAgreement && (
                                <div className="text-xs text-muted-foreground">
                                  Based on{' '}
                                  {
                                    suggestBaselineMutation.data
                                      .priorYearAgreement.year
                                  }{' '}
                                  transaction data
                                </div>
                              )}
                              <button
                                type="button"
                                className="w-full text-left p-3 rounded-md border hover:bg-accent transition-colors"
                                onClick={() => {
                                  handleInputChange(
                                    'growthBaseline',
                                    suggestBaselineMutation.data.suggestions!.actuals.value.toString(),
                                  );
                                  setSuggestionPopoverOpen(false);
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm">
                                    Last Year Actuals
                                  </span>
                                  <span className="font-semibold">
                                    {formatCurrency(
                                      suggestBaselineMutation.data.suggestions
                                        .actuals.value,
                                    )}
                                  </span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {suggestBaselineMutation.data.suggestions
                                    .actuals.monthsOfData === 0
                                    ? 'No data available'
                                    : `${suggestBaselineMutation.data.suggestions.actuals.monthsOfData} months of data through ${suggestBaselineMutation.data.suggestions.actuals.lastDataMonth}`}
                                </div>
                              </button>
                              <button
                                type="button"
                                className="w-full text-left p-3 rounded-md border hover:bg-accent transition-colors"
                                onClick={() => {
                                  handleInputChange(
                                    'growthBaseline',
                                    suggestBaselineMutation.data.suggestions!.predicted.value.toString(),
                                  );
                                  setSuggestionPopoverOpen(false);
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm">
                                    Last Year Predicted
                                  </span>
                                  <span className="font-semibold">
                                    {formatCurrency(
                                      suggestBaselineMutation.data.suggestions
                                        .predicted.value,
                                    )}
                                  </span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  Projected year-end value
                                </div>
                              </button>
                            </div>
                          )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Input
                  id="baseline"
                  type="number"
                  value={formData.growthBaseline}
                  onChange={(e) =>
                    handleInputChange('growthBaseline', e.target.value)
                  }
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            )}

          {!formData.isManual && (
            <div className="flex items-center space-x-2">
              <Switch
                id="retroactive"
                checked={formData.isRetroactiveToDollarOne}
                onCheckedChange={(checked) =>
                  handleInputChange('isRetroactiveToDollarOne', checked)
                }
              />
              <Label htmlFor="retroactive" className="text-sm">
                Retroactive to Dollar One
              </Label>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
            rows={2}
            maxLength={2000}
            placeholder="Optional notes..."
          />
        </div>

        {/* Custom Rule Duration */}
        <div className="space-y-3 pt-2 border-t">
          <div className="flex items-center gap-2">
            <Switch
              id="customDuration"
              checked={hasCustomDuration}
              onCheckedChange={(checked) => {
                setHasCustomDuration(checked);
                if (checked) {
                  // Default to agreement dates when enabling custom duration
                  const newFormData = {
                    ...formData,
                    startDate: agreementStartDate || null,
                    endDate: agreementEndDate || null,
                  };
                  setFormData(newFormData);
                  onUpdate(createUpdatePayload(newFormData, tiers));
                } else {
                  // Clear dates when toggling off
                  const newFormData = {
                    ...formData,
                    startDate: null,
                    endDate: null,
                  };
                  setFormData(newFormData);
                  onUpdate(createUpdatePayload(newFormData, tiers));
                }
              }}
            />
            <Label htmlFor="customDuration" className="text-sm">
              Custom rule duration
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <QuestionIcon className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    By default, rules are active for the entire agreement
                    period. Enable this to set a custom start and/or end date
                    for this rule.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {hasCustomDuration && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
              <div className="space-y-2">
                <Label htmlFor="ruleStartDate">Rule Start Date</Label>
                <DatePicker
                  value={
                    formData.startDate
                      ? new Date(formData.startDate + 'T00:00:00')
                      : undefined
                  }
                  placeholder="Select start date"
                  onSelect={(date: Date | undefined) => {
                    const dateStr = date ? formatDateToLocalString(date) : null;
                    handleInputChange('startDate', dateStr);
                  }}
                  disabled={(date: Date) => {
                    // Disable dates before agreement start
                    if (agreementStartDate) {
                      const minDate = new Date(
                        agreementStartDate + 'T00:00:00',
                      );
                      if (date < minDate) return true;
                    }
                    // Disable dates after rule end date or agreement end
                    const maxDateStr = formData.endDate || agreementEndDate;
                    if (maxDateStr) {
                      const maxDate = new Date(maxDateStr + 'T00:00:00');
                      if (date > maxDate) return true;
                    }
                    return false;
                  }}
                />
                {agreementStartDate && (
                  <p className="text-xs text-muted-foreground">
                    Agreement starts: {agreementStartDate}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="ruleEndDate">Rule End Date</Label>
                <DatePicker
                  value={
                    formData.endDate
                      ? new Date(formData.endDate + 'T00:00:00')
                      : undefined
                  }
                  placeholder="Select end date"
                  onSelect={(date: Date | undefined) => {
                    const dateStr = date ? formatDateToLocalString(date) : null;
                    handleInputChange('endDate', dateStr);
                  }}
                  disabled={(date: Date) => {
                    // Disable dates before rule start date or agreement start
                    const minDateStr = formData.startDate || agreementStartDate;
                    if (minDateStr) {
                      const minDate = new Date(minDateStr + 'T00:00:00');
                      if (date < minDate) return true;
                    }
                    // Disable dates after agreement end
                    if (agreementEndDate) {
                      const maxDate = new Date(agreementEndDate + 'T00:00:00');
                      if (date > maxDate) return true;
                    }
                    return false;
                  }}
                />
                {agreementEndDate && (
                  <p className="text-xs text-muted-foreground">
                    Agreement ends: {agreementEndDate}
                  </p>
                )}
              </div>
            </div>
          )}
          {validationErrors.dates && (
            <p className="text-sm text-red-500 pl-6">
              {validationErrors.dates}
            </p>
          )}
        </div>
      </div>

      {/* Divisions */}
      {!formData.isManual && (
        <div className="space-y-2">
          <MultiSelect
            label="Select Divisions"
            placeholder="Select divisions"
            options={divisions.map((div) => ({
              id: div.id,
              label: div.name,
              value: div,
              group: div.tags.length > 0 ? div.tags[0] : undefined,
            }))}
            value={formData.selectedDivisions.map((div) => ({
              id: div.id,
              label: div.name,
              value: div,
            }))}
            onChange={(selected) => {
              handleInputChange(
                'selectedDivisions',
                selected.map((option) => option.value as DivisionSimpleDTO),
              );
            }}
            searchPlaceholder="Search divisions"
            renderSelectedDisplay={(selectedItems, totalCount) => {
              if (selectedItems.length === totalCount && totalCount > 0) {
                return (
                  <div className="flex gap-1 mb-2">
                    <Badge variant="outline">All Divisions</Badge>
                  </div>
                );
              }
              return null;
            }}
          />
        </div>
      )}

      {/* Product Groups */}
      {!formData.isManual && (
        <div className="space-y-4">
          {/* Combine Toggle */}
          <div className="flex items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-colors',
                      combineProductGroups
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                    )}
                    onClick={() => handleCombineToggle(!combineProductGroups)}
                  >
                    {combineProductGroups ? (
                      <LinkIcon className="h-4 w-4" />
                    ) : (
                      <LinkBreakIcon className="h-4 w-4" />
                    )}
                    <span className="text-sm font-medium">
                      {combineProductGroups
                        ? 'Groups Linked'
                        : 'Groups Separate'}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <div className="space-y-2">
                    <p className="font-semibold">
                      Link Qualifying & Affected Groups
                    </p>
                    <p>
                      <strong>Linked:</strong> Same product groups are used for
                      both threshold calculations (qualifying) and rebate
                      application (affected).
                    </p>
                    <p>
                      <strong>Separate:</strong> Choose different product groups
                      for qualifying vs. affected independently.
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Product Group Selectors */}
          {combineProductGroups ? (
            <ProductGroupSelector
              label="Qualifying & Affected Product Groups"
              placeholder="Select product groups"
              inclusionType={
                formData.qualifyingInclusionType as ProductInclusionType
              }
              onInclusionTypeChange={(type) =>
                handleInputChange('qualifyingInclusionType', type)
              }
              selectedGroups={formData.qualifyingProductGroups}
              onGroupsChange={(groups) =>
                handleInputChange('qualifyingProductGroups', groups)
              }
              inclusionTypeName="qualifyingInclusionType"
              required
              productGroups={productGroups}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ProductGroupSelector
                label="Qualifying Product Groups"
                placeholder="Select qualifying products"
                inclusionType={
                  formData.qualifyingInclusionType as ProductInclusionType
                }
                onInclusionTypeChange={(type) =>
                  handleInputChange('qualifyingInclusionType', type)
                }
                selectedGroups={formData.qualifyingProductGroups}
                onGroupsChange={(groups) =>
                  handleInputChange('qualifyingProductGroups', groups)
                }
                inclusionTypeName="qualifyingInclusionType"
                productGroups={productGroups}
              />

              <ProductGroupSelector
                label="Affected Product Groups"
                placeholder="Select affected products"
                inclusionType={
                  formData.affectedInclusionType as ProductInclusionType
                }
                onInclusionTypeChange={(type) =>
                  handleInputChange('affectedInclusionType', type)
                }
                selectedGroups={formData.affectedProductGroups}
                onGroupsChange={(groups) =>
                  handleInputChange('affectedProductGroups', groups)
                }
                inclusionTypeName="affectedInclusionType"
                required
                productGroups={productGroups}
              />
            </div>
          )}
        </div>
      )}

      {/* Tiers */}
      {!formData.isManual && (
        <div className="space-y-4">
          <h4 className="font-semibold">Rebate Tiers</h4>
          {tiers.map((tier, index) => {
            const isExpanded = expandedTiers.has(index);
            const isEstimateTier = tierStatus?.estimates === tier.tierOrder;
            const isPredictionTier = tierStatus?.predictions === tier.tierOrder;
            const isReportedTier = tierStatus?.reported === tier.tierOrder;
            return (
              <Card key={index}>
                <div
                  className="flex justify-between items-center p-4 cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleTierExpansion(index)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-blue-500 text-white">
                      {tier.tierOrder}
                    </div>
                    {!isExpanded && (
                      <div className="text-sm font-medium">
                        {tier.thresholdValue
                          ? formatThresholdRange(
                              tiers,
                              tier.tierOrder,
                              formData.thresholdType,
                              formData.growthBaseline,
                            )
                          : 'No threshold'}{' '}
                        → {tier.rebateValue || '0'}% rebate
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {tierStatus && (
                      <div className="flex gap-1">
                        {isEstimateTier && (
                          <Badge
                            variant="outline"
                            className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"
                          >
                            Est
                          </Badge>
                        )}
                        {isPredictionTier && (
                          <Badge
                            variant="outline"
                            className="bg-blue-50 text-blue-700 border-blue-200 text-xs"
                          >
                            Proj
                          </Badge>
                        )}
                        {isReportedTier && (
                          <Badge
                            variant="outline"
                            className="bg-gray-50 text-gray-700 border-gray-200 text-xs"
                          >
                            Reported
                          </Badge>
                        )}
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTierExpansion(index);
                      }}
                      className="h-8 w-8 p-0"
                    >
                      {isExpanded ? (
                        <CaretUpIcon className="h-4 w-4" />
                      ) : (
                        <CaretDownIcon className="h-4 w-4" />
                      )}
                    </Button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateTier(index);
                            }}
                            className="h-8 w-8 p-0 text-blue-600"
                          >
                            <CopyIcon className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Duplicate tier</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {tiers.length > 1 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeTier(index);
                              }}
                              className="h-8 w-8 p-0 text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Delete tier</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>
                          {formData.thresholdType === ThresholdType.GROWTH
                            ? 'Growth Threshold (%)'
                            : 'Threshold Value ($)'}{' '}
                          *
                        </Label>
                        <Input
                          type="number"
                          value={tier.thresholdValue}
                          onChange={(e) =>
                            handleTierChange(
                              index,
                              'thresholdValue',
                              e.target.value,
                            )
                          }
                          min="0"
                          step="0.01"
                          className={
                            validationErrors.tiers?.[index]?.thresholdValue
                              ? 'border-red-500'
                              : ''
                          }
                          required
                        />
                        {validationErrors.tiers?.[index]?.thresholdValue && (
                          <p className="text-sm text-red-600">
                            {validationErrors.tiers[index].thresholdValue}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Rebate Percentage (%) *</Label>
                        <Input
                          type="number"
                          value={tier.rebateValue}
                          onChange={(e) =>
                            handleTierChange(
                              index,
                              'rebateValue',
                              e.target.value,
                            )
                          }
                          min="0"
                          max="100"
                          step="0.01"
                          className={
                            validationErrors.tiers?.[index]?.rebateValue
                              ? 'border-red-500'
                              : ''
                          }
                          required
                        />
                        {validationErrors.tiers?.[index]?.rebateValue && (
                          <p className="text-sm text-red-600">
                            {validationErrors.tiers[index].rebateValue}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
          <div className="flex justify-center">
            <PrimaryButton
              onClick={addTier}
              startIcon={<PlusIcon />}
              size="small"
            >
              Add Tier
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* Validation Error Summary */}
      {!isFormValid && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-semibold text-red-800 mb-2">
            Please fix the following errors before saving:
          </p>
          <ul className="space-y-1">
            {validationErrors.name && (
              <li className="text-sm text-red-700">
                • {validationErrors.name}
              </li>
            )}
            {!formData.isManual && validationErrors.growthBaseline && (
              <li className="text-sm text-red-700">
                • {validationErrors.growthBaseline}
              </li>
            )}
            {validationErrors.discountPercentage && (
              <li className="text-sm text-red-700">
                • {validationErrors.discountPercentage}
              </li>
            )}
            {validationErrors.divisions && (
              <li className="text-sm text-red-700">
                • {validationErrors.divisions}
              </li>
            )}
            {!formData.isManual &&
              validationErrors.productGroups?.qualifyingProductGroups && (
                <li className="text-sm text-red-700">
                  •{' '}
                  {combineProductGroups
                    ? 'Product Groups: '
                    : 'Qualifying Product Groups: '}
                  {validationErrors.productGroups.qualifyingProductGroups}
                </li>
              )}
            {!formData.isManual &&
              !combineProductGroups &&
              validationErrors.productGroups?.affectedProductGroups && (
                <li className="text-sm text-red-700">
                  • Affected Product Groups:{' '}
                  {validationErrors.productGroups.affectedProductGroups}
                </li>
              )}
            {!formData.isManual && validationErrors.tiers && (
              <li className="text-sm text-red-700">
                • Please fix tier validation errors above
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
